import { cloneDeep } from 'lodash';
import { KnownClientId } from '@app/const';
import {
  AvsChangeLogs,
  AvsFileName,
  BehaviorSubject,
  BranchInfo,
  Design,
  DesignBranchingEventType,
  DesignTags,
  LoadingProgressStatus
} from '@app/types';
import {
  Case,
  compareDesignVersions,
  convertCase,
  getAllPages,
  getLatestBranchingEvent,
  loopObjectsAndNodes,
  normalizeCsvs,
  normalizeGraphqlSchemas,
  normalizeIntentCollections,
  normalizeLegacySettings,
  normalizeModules,
  normalizeObjectOrNode,
  normalizeRootSettings,
  PageInfo
} from '@app/utilities';
import { IErrorMessageService } from './error-message-service';
import { IHttpService } from './http-service';
import { IIntentManagementService } from './intent-management-service';
import {
  AvsBranch,
  AvsClient,
  AvsCreateTagProps,
  AvsCreateTagResponse,
  AvsDesign,
  AvsErrorResponse,
  AvsMetadataItem,
  AvsPaginatedResponse,
  AvsVersion,
  DesignInfo,
  DocumentsForSave,
  HttpFetchProps,
  HttpMethod
} from './types';

type NewClient = Pick<AvsClient, (
  'code'
  | 'description'
  | 'designName'
  | 'name'
)>;

type GetChangeLogsConfig = {
  /** Amount of change logs to try fetching. */
  count?: number;
};

/** Service used to connect to all Application Versioning Service APIs. */
export class AppVersioningService {
  /** Base URL to add to all HTTP requests. */
  static readonly baseUrl = '/versioning/appversioning';
  /** Branch name of the primary branch for each @see AvsDesign. */
  static readonly mainBranchName = 'Main';

  private _errorMessageService: IErrorMessageService;
  private _httpService: IHttpService;
  private _intentService: IIntentManagementService;

  /** Branch name of the primary branch for each @see AvsDesign. */
  get mainBranchName(): string {
    return AppVersioningService.mainBranchName;
  }

  constructor(
    errorMessageService: IErrorMessageService,
    httpService: IHttpService,
    intentService: IIntentManagementService
  ) {
    this._errorMessageService = errorMessageService;
    this._httpService = httpService;
    this._intentService = intentService;
  }

  /** Save a new branch to AVS. */
  async createBranch(
    designId: string,
    branch: Pick<AvsBranch, 'd2Client' | 'd2Design' | 'id' | 'name'>
  ): Promise<AvsBranch> {
    await this._fetchJsonOrThrow<AvsBranch>(
      'Failed to create AVS branch.',
      {
        body: branch,
        method: HttpMethod.post,
        url: `api/v2/designs/${designId}/branches`
      }
    );

    // The save API does not set some fields, so an update is required.
    return await this.updateBranch({
      ...branch
    });
  }

  /** Save a new AVS client to AVS. */
  async createClient(
    clientOrName: NewClient | string
  ): Promise<AvsClient> {
    const newClient: NewClient = (typeof clientOrName === 'object') ? (
      clientOrName
    ) : {
      code: convertCase(clientOrName, Case.kebab),
      name: clientOrName,
      description: convertCase(clientOrName, Case.title),
      designName: clientOrName
    };

    const form = new FormData();
    form.append('client', new Blob([JSON.stringify(newClient)], {
      type: 'application/json'
    }));

    return await this._fetchJsonOrThrow<AvsClient>(
      `Failed to create client '${newClient.name}'`,
      {
        body: form,
        method: HttpMethod.post,
        url: 'api/v1/clients'
      }
    );
  }

  /** Save a new AVS design (not to be confused with @see Design) to AVS. */
  async createDesign(
    clientId: string,
    design: Pick<AvsDesign, 'name'> & Partial<Pick<AvsDesign, 'id'>>
  ): Promise<AvsDesign> {
    return await this._fetchJsonOrThrow<AvsDesign>(
      'Failed to create AVS design.',
      {
        body: design,
        method: HttpMethod.post,
        url: `api/v2/clients/${clientId}/designs`
      }
    );
  }

  /** Add a tag to a @see Design. */
  async createTag<K extends keyof(DesignTags)>({
    branchId,
    metaData,
    tag,
    version
  }: AvsCreateTagProps<K>): Promise<AvsCreateTagResponse | null> {
    return (await this._httpService.fetchJson<AvsCreateTagResponse>({
      url: `api/v2/branches/${branchId}/versions/${version}/tags`,
      method: HttpMethod.post,
      body: { metaData, tag }
    })).value ?? null;
  }

  /** Save a new version of a branch. */
  async createVersion(
    branchId: string,
    version: string,
    versionData: FormData
  ): Promise<void> {
    const response = await this._httpService.fetch(
      {
        body: versionData,
        method: HttpMethod.post,
        url: `api/v2/branches/${branchId}/versions/${version}/upload`
      }
    );

    if (!response.ok) {
      throw await this._createErrorFromResponse(
        'Failed to upload version to AVS.',
        response
      );
    }
  }

  /** Get a list of arch type designs to use as templates for new designs. */
  async getArchTypeDesigns(): Promise<AvsDesign[]> {
    return this.getDesigns(KnownClientId.archType);
  }

  /** Gets information about the provided branch. */
  async getBranchInfo(
    branchId: string
  ): Promise<BranchInfo | null> {
    return (await this._httpService.fetchJson<BranchInfo>({
      url: `api/v2/branches/${branchId}`
    })).value ?? null;
  }

  /** Get a list of branches associated with a given design. */
  async getBranches(designId: string): Promise<AvsBranch[]> {
    const branches = (await this._httpService.fetchJson<AvsBranch[]>({
      url: `api/v2/designs/${designId}/branches`
    })).value ?? [];

    // Splice out main branch and insert it first.
    const mainIndex = branches.findIndex((branch) => {
      return branch.name === this.mainBranchName;
    });
    const mainBranch = branches.splice(mainIndex, 1);
    return [
      ...mainBranch,
      ...branches.sort((a, b) => {
        return a.name.localeCompare(b.name);
      })
    ];
  }

  /**
   * Get change logs for the provided branch and version.
   *
   * We only keep the last 20 entries in AVS so @see GetChangeLogsConfig.count
   * will make paged calls to reach the count.
   */
  async getChangeLogs(
    branchId: string,
    version: string,
    { count }: GetChangeLogsConfig = {}
  ): Promise<AvsChangeLogs[]> {
    const getPage = (pageVersion: string) => {
      return this._getAvsFile<AvsChangeLogs[]>(
        branchId,
        pageVersion,
        AvsFileName.changeLogMaps,
        []
      );
    };

    if (count == null) {
      return await getPage(version);
    }

    const versions = new Set<string>();
    const changeLogs: AvsChangeLogs[] = [];
    let pageVersion = version;
    while (changeLogs.length < count) {
      const pageOfChangeLogs = (
        await getPage(pageVersion)
      ).filter((changeLog) => !versions.has(changeLog.version));

      pageOfChangeLogs.forEach((changeLog) => versions.add(changeLog.version));
      changeLogs.push(...pageOfChangeLogs);
      // Pages come back newest-first, so the oldest entry's version is the next page.
      pageVersion = changeLogs[changeLogs.length - 1]?.version ?? '';
      if (!pageOfChangeLogs.length || pageVersion === '1.0.0') {
        break;
      }
    }
    return changeLogs.slice(0, count);
  }

  /** Get a client by ID. */
  getClient(clientId: string): Promise<AvsClient> {
    return this._httpService.fetchJsonOrFail({
      url: `api/v1/clients/${clientId}`
    });
  }

  /** Get a list of clients. */
  async getClients(): Promise<AvsClient[]> {
    const clients = await this._getAllPages<AvsClient>(
      'clientResourceList',
      ({ pageI, pageSize }) => {
        return `api/v1/clients?page=${pageI}&size=${pageSize}`;
      }
    );

    return clients.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }

  /**
   * Get a common module's @see Design by @see AvsDesign.name & @see Design.version.
   *
   * @deprecated This is not ideal, but branch IDs were added after imported modules.
   */
  async getCommonModuleDesignByName(
    avsDesignName: string,
    version?: string
  ): Promise<Design> {
    const branch = await this.getCommonModuleMainBranchByDesignName(
      avsDesignName
    );
    version ??= (!branch) ? undefined : (await this.getLatestVersion(branch.id))?.version;
    const design = (!branch || !version) ? null : await this.getDesign(branch.id, version);
    if (design) {
      return design;
    }

    const error = new Error(
      `Unable to get '${avsDesignName}' (${version ?? 'latest version'}).`
    );
    await this._errorMessageService.addError(error);
    throw error;
  }

  /** Get a list of common modules. */
  async getCommonModuleDesigns(): Promise<AvsDesign[]> {
    return this.getDesigns(KnownClientId.interactionsCommon);
  }

  /**
   * Get a common module's main branch by its design's name.
   *
   * @deprecated This is not ideal, but branch IDs were added after imported modules.
   */
  async getCommonModuleMainBranchByDesignName(
    commonModuleDesignName: string
  ): Promise<AvsBranch | null> {
    commonModuleDesignName = commonModuleDesignName.toLowerCase();
    const designId = (await this.getCommonModuleDesigns()).find((design) => {
      return design.name?.toLocaleLowerCase() === commonModuleDesignName;
    })?.id;

    return (!designId) ? null : this.getMainBranch(designId);
  }

  /** Get a design using its branch and version. */
  async getDesign(
    branchId: string,
    version: string
  ): Promise<Design> {
    const { latestSerialized } = window.apm ?? {};
    if (
      latestSerialized
      && latestSerialized.branchId === branchId
      && latestSerialized.version === version
    ) {
      return cloneDeep(latestSerialized);
    }

    const design = await this._getAvsFile<Design>(
      branchId,
      version,
      AvsFileName.design
    );

    design.designer ??= '1.0';
    design.repoClient ??= design.client;
    design.repoApplication ??= design.application;
    design.repoVersion ??= design.version;

    const [
      branchInfo,
      unitTests
    ] = await Promise.all([
      this.getBranchInfo(branchId),
      this.getUnitTests(branchId, version, []),
      this.hydrateDesign(design),
      this._intentService.hydrateDesign(design)
    ]);

    design.unitTests = unitTests;

    if (branchInfo) {
      design.designName = branchInfo.designName;
    }

    this.normalizeSerialized(design);

    return design;
  }

  /** Gets information about the provided branch. */
  async getDesignInfo(
    designId: string
  ): Promise<DesignInfo | null> {
    return (await this._httpService.fetchJson<DesignInfo>({
      url: `api/v2/designs/${designId}`
    })).value ?? null;
  }

  /** Get a list of designs associated with a given client. */
  async getDesigns(clientId: string): Promise<AvsDesign[]> {
    const designs = (await this._httpService.fetchJson<AvsDesign[]>({
      url: `api/v2/clients/${clientId}/designs`
    })).value ?? [];

    return designs.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }

  /** Get all of the saved data needed to merge branch into main. */
  async getDocumentsForMerge(
    branchId: string,
    mainDesign: Design
  ): Promise<DocumentsForSave> {
    const timeId = 'Get Documents For Merge';
    console.time(timeId);

    // Start with the last version merged into main or the first version if it has not been.
    const branchDivergedVersion = getLatestBranchingEvent(
      mainDesign.branchingMap?.[branchId],
      DesignBranchingEventType.merge
    )?.merged_at ?? '1.0.0';
    const branchLatestVersion = (
      await this.getLatestVersion(branchId)
    )?.version;
    if (!branchLatestVersion) {
      throw new Error(`Unable to find latest version for branch '${branchId}'.`);
    }

    const branchDivergedDesignPromise = this.getDesign(
      branchId,
      branchDivergedVersion
    );
    const branchLatestDesignPromise = (branchLatestVersion === branchDivergedVersion) ? (
      branchDivergedDesignPromise
    ) : this.getDesign(branchId, branchLatestVersion);
    const [
      branchDivergedDesign,
      branchLatestDesign
    ] = await Promise.all([
      branchDivergedDesignPromise,
      branchLatestDesignPromise
    ]);

    // Main diverged from branch when it was last pulled in or when the branch was created.
    const mainDivergedEvent = getLatestBranchingEvent(
      branchLatestDesign.branching,
      [DesignBranchingEventType.initBranch, DesignBranchingEventType.pull]
    );
    if (!mainDivergedEvent) {
      throw new Error('Unable to figure out which version of Main the branch was created at.');
    }
    const mainDivergedVersion = (mainDivergedEvent.type === DesignBranchingEventType.initBranch) ? (
      mainDivergedEvent.branched_at
    ) : mainDivergedEvent.pulled_at;
    const mainDivergedDesign = await this.getDesign(mainDesign.branchId, mainDivergedVersion);

    console.timeEnd(timeId);
    return {
      sourceDesign: branchLatestDesign,
      sourceOriginDesign: branchDivergedDesign,
      targetDesign: mainDesign,
      targetOriginDesign: mainDivergedDesign
    };
  }

  /** Get all of the saved data needed to pull main into a branch. */
  async getDocumentsForPull(branchLatestDesign: Design): Promise<DocumentsForSave> {
    const timeId = 'Get Documents For Pull';
    console.time(timeId);

    // Main diverged from branch when it was last pulled in or when the branch was created.
    const mainDivergedEvent = getLatestBranchingEvent(
      branchLatestDesign.branching,
      [DesignBranchingEventType.initBranch, DesignBranchingEventType.pull]
    );
    const mainId = getLatestBranchingEvent(
      branchLatestDesign.branching,
      DesignBranchingEventType.initBranch
    )?.parent_branch_id;
    if (!mainDivergedEvent || !mainId) {
      throw new Error('Unable to figure out which version of Main the branch was created at.');
    }
    const mainDivergedVersion = (mainDivergedEvent.type === DesignBranchingEventType.initBranch) ? (
      mainDivergedEvent.branched_at
    ) : mainDivergedEvent.pulled_at;
    const mainLatestVersion = (await this.getLatestVersion(mainId))?.version;
    if (!mainLatestVersion) {
      throw new Error(`Unable to find latest version for main '${mainId}'.`);
    }

    const mainDivergedDesignPromise = this.getDesign(mainId, mainDivergedVersion);
    const mainLatestDesignPromise = (mainLatestVersion === mainDivergedVersion) ? (
      mainDivergedDesignPromise
    ) : this.getDesign(mainId, mainLatestVersion);
    const [
      mainDivergedDesign,
      mainLatestDesign
    ] = await Promise.all([
      mainDivergedDesignPromise,
      mainLatestDesignPromise
    ]);

    const { branchId } = branchLatestDesign;
    const branchDivergedVersion = getLatestBranchingEvent(
      mainLatestDesign.branchingMap?.[branchLatestDesign.branchId] ?? [],
      DesignBranchingEventType.merge
    )?.merged_at ?? '1.0.0';

    const branchDivergedDesign = await this.getDesign(branchId, branchDivergedVersion);

    console.timeEnd(timeId);
    return {
      sourceDesign: mainLatestDesign,
      sourceOriginDesign: mainDivergedDesign,
      targetDesign: branchLatestDesign,
      targetOriginDesign: branchDivergedDesign
    };
  }

  /** Get all of the saved data needed to create a new version. */
  async getDocumentsForSave(localDesign: Design): Promise<DocumentsForSave> {
    const timeId = 'Get Documents For Save';
    console.time(timeId);

    const { branchId, version: originVersion } = localDesign;
    // Latest version of @see localDesign.
    let latestDesign: Design | null = null;
    // Version that @see localDesign started as to find changes on it and @see latestDesign.
    let originDesign: Design | null = null;
    const targetDesignVersion = (await this.getLatestVersion(
      branchId
    ))?.version;

    if (targetDesignVersion) {
      // Fetch designs if this branch has been saved before.
      const originDesignPromise = (
        this.getDesign(branchId, originVersion)
      );
      [originDesign, latestDesign] = await Promise.all([
        originDesignPromise,
        (
          (targetDesignVersion === originVersion)
            ? originDesignPromise
            : this.getDesign(branchId, targetDesignVersion)
        )
      ]);
    } else {
      // Intentionally left blank since unsaved branches do not have designs to fetch.
    }

    console.timeEnd(timeId);
    return {
      sourceDesign: localDesign,
      sourceOriginDesign: originDesign,
      targetDesign: latestDesign,
      targetOriginDesign: originDesign
    };
  }

  /**
   * Get the latest version associated with a given branch.
   *
   * @note Returns `null` when provided a branch without any saved versions.
   */
  async getLatestVersion(branchId: string): Promise<AvsVersion | null> {
    const {
      error,
      value
    } = (await this._httpService.fetchJson<AvsVersion[] | null>({
      url: `api/v2/branches/${branchId}/versions?latest=true`
    }));

    if (
      error instanceof Response
      && error.status === 404
    ) {
      return null;
    } else if (error) {
      throw error;
    }

    return value?.[0] ?? null;
  }

  /** Gets the main branch for a design. */
  async getMainBranch(
    designId: string
  ): Promise<AvsBranch | null> {
    return (
      await this.getBranches(designId)
    ).find((branch) => {
      return branch.name === this.mainBranchName;
    }) ?? null;
  }

  /** Get metadata for the provided branch/version. */
  async getMetadata(
    branchId: string,
    version: string
  ): Promise<AvsMetadataItem[] | null> {
    return (await this._httpService.fetchJson<AvsMetadataItem[]>({
      url: `api/v1/branches/${branchId}/metadata?version=${version}`
    })).value ?? null;
  }

  /** Get unit tests using its branch and version. */
  async getUnitTests(
    branchId: string,
    version: string,
    fallback?: Design['unitTests']
  ): Promise<Design['unitTests']> {
    return this._getAvsFile<Design['unitTests']>(
      branchId,
      version,
      AvsFileName.unitTests,
      fallback
    );
  }

  /** Get a list of versions associated with a given branch. */
  async getVersions(branchId: string): Promise<AvsVersion[]> {
    const versions = (await this._httpService.fetchJson<AvsVersion[]>({
      url: `api/v2/branches/${branchId}/versions`
    })).value ?? [];

    // Sort the response by version in descending order.
    return versions.sort((a, b) => {
      return -1 * compareDesignVersions(a.version, b.version);
    });
  }

  /** Adds branch information to a design if it is not already present. */
  async hydrateDesign(design: Design): Promise<void> {
    if (this.isHydrated(design)) {
      return;
    }

    const branchInfo = await this.getBranchInfo(design._id);
    if (branchInfo == null) {
      await this._errorMessageService.addError({
        onClose: () => {
          window.location.href = `/${location.search}`;
        },
        message: `Branch info not found for design ${design.application} (${design._id})`,
        title: 'Design branch info missing'
      });

      return;
    }
    this._assignBranchInfoFields(design, branchInfo);
  }

  /** Hydrates an imported design when possible or assumes it is a new @see AvsDesign. */
  async hydrateImportedDesign(importedDesign: Design): Promise<void> {
    if (this.isHydrated(importedDesign)) {
      return;
    }

    const branchInfo = await this.getBranchInfo(importedDesign._id);
    if (branchInfo !== null) {
      this._assignBranchInfoFields(importedDesign, branchInfo);
      return;
    }

    // Client fields.
    const clients = await this.getClients();
    const client = clients.find((c) => c.name === importedDesign.client);
    if (client) {
      importedDesign.clientId = client.id;
      importedDesign.clientName = client.name;
    } else {
      throw new Error(`Client with name '${importedDesign.client}' not found.`);
    }

    // Design fields.
    const designs = await this.getDesigns(client.id) || [];
    const design = designs.find((d) => d.name === importedDesign.application);
    if (design) {
      importedDesign.designId = design.id;
      importedDesign.designName = design.name;
    } else {
      // Design & branch are created on first save.
      importedDesign.designName = importedDesign.application;
    }

    // Branch fields.
    const branches = (design) ? await this.getBranches(design.id) : [];
    const branch = branches.find((b) => b.name === this.mainBranchName);
    if (branch) {
      importedDesign.branchId = branch.id;
      importedDesign.branchName = branch.name;
    } else {
      importedDesign.branchId = importedDesign._id;
      importedDesign.branchName = this.mainBranchName;
    }
  }

  /** Checks if branch information has already been added to a design. */
  isHydrated(design: Design): boolean {
    return !!design?.branchId;
  }

  /** Correct safe to fix issues in @see serialized. */
  normalizeSerialized(serialized: Design): void {
    normalizeIntentCollections(serialized);
    normalizeCsvs(serialized);
    normalizeGraphqlSchemas(serialized);
    normalizeLegacySettings(serialized);
    normalizeModules(serialized);
    normalizeRootSettings(serialized);

    loopObjectsAndNodes({
      callback: ({ appModule, item }) => {
        normalizeObjectOrNode(item, serialized, appModule ?? undefined);
      },
      serialized
    });
  }

  /** Save a new version of a branch, publishing upload progress to @see progressMonitor. */
  async saveVersion(
    branchId: string,
    version: string,
    versionData: FormData,
    progressMonitor?: BehaviorSubject<LoadingProgressStatus>
  ): Promise<void> {
    const response = await this._httpService.fetch({
      body: versionData,
      method: HttpMethod.post,
      progressMonitor,
      url: `api/v2/branches/${branchId}/versions/${version}/upload`
    });

    if (!response.ok) {
      throw await this._createErrorFromResponse(
        'Failed to upload version to AVS.',
        response
      );
    }
  }

  /** Update an existing branch in AVS. */
  async updateBranch<K extends keyof (AvsBranch)>(
    {
      id: branchId,
      ...branch
    }: Pick<AvsBranch, 'id' | K>
  ): Promise<AvsBranch> {
    return this._fetchJsonOrThrow(
      'Failed to update AVS branch.',
      {
        body: branch,
        method: HttpMethod.put,
        url: `api/v2/branches/${branchId}`
      }
    );
  }

  /** Update an existing AVS design in AVS. */
  async updateDesign<T extends keyof AvsDesign>({
    id,
    ...body
  }: Pick<AvsDesign, T | 'id'>): Promise<AvsDesign> {
    return await this._fetchJsonOrThrow<AvsDesign>(
      'Failed to change AVS design.',
      {
        body,
        method: HttpMethod.put,
        url: `api/v2/designs/${id}`
      }
    );
  }

  /** Assign @see BranchInfo fields to @see Design. */
  private _assignBranchInfoFields(design: Design, branchInfo: BranchInfo) {
    Object.assign(design, {
      designId: branchInfo.designId,
      designName: branchInfo.designName,
      branchId: branchInfo.id,
      branchName: branchInfo.name,
      clientId: branchInfo.clientId,
      clientName: branchInfo.clientName
    });
  }

  /** Create an error from an error/response. */
  private async _createErrorFromResponse(
    errorMessage: string,
    error: Error | Response
  ): Promise<Error> {
    if (error instanceof Error) {
      return error;
    }

    let message: string | null = null;
    let json: unknown = null;
    try {
      message = await error.text();
      json = JSON.parse(message);
      if (AvsErrorResponse.is(json)) {
        return AvsErrorResponse.createError(errorMessage, json);
      }
    } catch {
      // Intentionally left blank.
    }

    return new Error(message || errorMessage);
  }

  /** Fetch JSON or throw an error. */
  private async _fetchJsonOrThrow<T>(
    errorMessage: string,
    request: HttpFetchProps
  ): Promise<T> {
    const { error, value } = await this._httpService.fetchJson<T>(
      request
    );

    if (!error) {
      return value;
    }

    throw await this._createErrorFromResponse(
      errorMessage,
      error
    );
  }

  /** Get all pages from an AVS API. */
  private _getAllPages<TItem>(
    key: string,
    getUrl: (pageInfo: PageInfo) => string
  ): Promise<TItem[]> {
    return getAllPages<AvsPaginatedResponse<string, TItem>, TItem>({
      getItems: (page) => {
        return page._embedded[key];
      },
      getPage: async (pageInfo) => {
        const url = getUrl(pageInfo);
        return this._fetchJsonOrThrow<AvsPaginatedResponse<string, TItem>>(
          `Failed to get page '${url}'.`,
          { url }
        );
      },
      getTotalItemCount: (page) => {
        return page.page.totalElements;
      }
    });
  }

  /** Get a file from AVS. */
  private async _getAvsFile<T>(
    branchId: string,
    version: string,
    file: AvsFileName,
    fallback?: T
  ): Promise<T> {
    const url = `api/v2/branches/${branchId}/versions/${version}/download?file=${file}`;
    const { error, value } = await this._httpService.fetchJson<T>({ url });

    if (value) {
      return value;
    } else if (fallback) {
      return fallback;
    }

    throw error;
  }
}
